import * as RxCollapsible from "@radix-ui/react-collapsible";
import { useControllableState } from "@radix-ui/react-use-controllable-state";
import cx from "clsx";
import ButtonTransparent from "kreate-common/modules/kreate-ui/components/ButtonTransparent";
import Flex from "kreate-common/modules/kreate-ui/components/Flex";
import React from "react";
import { MdKeyboardArrowDown, MdKeyboardArrowUp } from "react-icons/md";

import styles from "./index.module.scss";

type Props = {
  className?: string;
  style?: React.CSSProperties;
  showLabelAs: "h1" | "h2" | "h3" | "h4" | "h5" | "h6" | "div";
  label?: React.ReactNode;
  children?: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  defaultOpen?: boolean;
};

export default function CollapsibleFieldSet({
  className,
  style,
  showLabelAs,
  label,
  children,
  open: open$FromProps,
  onOpenChange: onOpenChange$FromProps,
  defaultOpen,
}: Props) {
  const Label = showLabelAs;

  // We are using @radix-ui/react-use-controllable-state so that this
  // component can be either controlled or uncontrolled.
  const [open = false, setOpen] = useControllableState<boolean>({
    prop: open$FromProps,
    defaultProp: defaultOpen,
    onChange: onOpenChange$FromProps,
  });

  const id = React.useId();

  return (
    <div className={cx(styles.container, className)} style={style}>
      <RxCollapsible.Root open={open} onOpenChange={setOpen}>
        <label htmlFor={id} style={{ cursor: "pointer" }}>
          <Flex.Row
            boxSizing="border-box"
            minHeight="64px"
            padding="16px 24px"
            justifyContent="space-between"
            alignItems="center"
          >
            <Label className={styles.label}>{label}</Label>
            <Flex.Cell flex="0 0 auto">
              <RxCollapsible.CollapsibleTrigger asChild>
                <ButtonTransparent
                  id={id}
                  icon={open ? <MdKeyboardArrowUp /> : <MdKeyboardArrowDown />}
                  onClick={(event) => {
                    event.preventDefault();
                    setOpen((open) => !open);
                  }}
                />
              </RxCollapsible.CollapsibleTrigger>
            </Flex.Cell>
          </Flex.Row>
        </label>
        <RxCollapsible.CollapsibleContent className={styles.content}>
          {children}
        </RxCollapsible.CollapsibleContent>
      </RxCollapsible.Root>
    </div>
  );
}
